import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { Award, TrendingUp, Target, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const GRADE_COLORS = { A: "#22c55e", B: "#3b82f6", C: "#f59e0b", D: "#ef4444", E: "#94a3b8" };

export default function QualityScorePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pharmatech/quality-score`, { headers: getHeaders() }).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <PharmatechLayout title="Score Qualité" subtitle="Évaluation de la performance pharmaceutique"><div className="flex justify-center py-20 text-slate-400">Chargement...</div></PharmatechLayout>;

  const gradeColor = GRADE_COLORS[data.grade] || "#94a3b8";

  return (
    <PharmatechLayout title="Score Qualite Pharmacie" subtitle="Benchmark et evaluation des performances">
      <div data-testid="quality-score-page" className="max-w-3xl mx-auto space-y-6">
        {/* Main score */}
        <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={gradeColor} strokeWidth="10"
                strokeDasharray={`${data.score * 3.27} 327`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-black" style={{ color: gradeColor }}>{data.grade}</span>
              <span className="text-xs text-slate-500">{data.score}/100</span>
            </div>
          </div>
          <h2 className="text-lg font-bold text-slate-800">{data.label}</h2>
          <p className="text-xs text-slate-500 mt-1">Score de qualite base sur votre utilisation des outils Pharmatech</p>
        </div>

        {/* Indicators */}
        <div className="space-y-3">
          {data.indicators.map((ind, i) => {
            const pct = ind.max > 0 ? (ind.points / ind.max) * 100 : 0;
            return (
              <div key={i} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid={`indicator-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{ind.label}</p>
                    <p className="text-[10px] text-slate-500">{ind.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-blue-600">{ind.points}</span>
                    <span className="text-xs text-slate-400">/{ind.max}</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{ind.value} action(s) enregistree(s)</p>
              </div>
            );
          })}
        </div>

        {/* Recommendations */}
        <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-500" /> Recommandations pour ameliorer votre score
          </h3>
          <div className="space-y-2">
            {data.indicators.filter(ind => ind.points < ind.max).map((ind, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white/70">
                <TrendingUp className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-700">
                  <strong>{ind.label}</strong>: {ind.points < ind.max * 0.3 ? "Commencez à utiliser cette fonctionnalité pour gagner des points." : "Continuez à utiliser cette fonctionnalité régulièrement."}
                </p>
              </div>
            ))}
            {data.score >= 80 && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <p className="text-xs text-green-700 font-medium">Excellent ! Vous êtes un modèle d'excellence pharmaceutique.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PharmatechLayout>
  );
}
